import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { formatDayTime, formatMoney } from '@dorixona/utils'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { Failed, Loading } from '../../ui/Load'
import { useSession } from '../../app/session'
import { loginPath } from '../../app/loginReturn'
import { TransactionRows } from './AccountParts'
import { useLoyalty, useOrders, useTransactions } from './api'
import { isOrderClosed, reservationOf, statusText } from './model'
import { usePoints } from '../../ui/points'

type HistoryTab = 'orders' | 'points'

/** Состояние перехода: пришли с карты лояльности на главной, а не вкладкой. */
export interface OrdersFrom {
  fromHome?: boolean
}

/**
 * «Мои заказы и баллы» — вкладка нижней панели, поэтому стрелки «назад» нет.
 * Исключение — пришли нажатием на карту лояльности (`state.fromHome`): тогда
 * стрелка ведёт обратно на главную. Сверху баланс и число заказов, под ними две вкладки — заказы и история
 * баллов. Вкладка живёт в адресе (`?tab=points`), поэтому «Назад» из карточки
 * заказа возвращает туда же. Гостю вместо списков — по центру экрана
 * объяснение, что заказывать можно после входа, и кнопка «Войти».
 */
export function OrdersScreen() {
  const navigate = useNavigate()
  const { isAuthenticated } = useSession()
  const { t } = useTranslation(['account', 'common'])
  const { formatPoints } = usePoints()
  const [params, setParams] = useSearchParams()
  const from = useLocation().state as OrdersFrom | null
  const tab: HistoryTab = params.get('tab') === 'points' ? 'points' : 'orders'

  const loyalty = useLoyalty(isAuthenticated)
  const orders = useOrders(isAuthenticated)
  const transactions = useTransactions(isAuthenticated)
  const orderList = orders.data ?? []

  const tabs: [HistoryTab, string][] = [
    ['orders', t('account:history.orders')],
    ['points', t('account:history.points')],
  ]

  const head = (
    <div className="mhead">
      {from?.fromHome ? <BackButton to="/" /> : null}
      <h1>{t('account:history.title')}</h1>
    </div>
  )

  if (!isAuthenticated) {
    return (
      <section className="screen cart-wrap">
        {head}
        <div className="card empty">
          <Icon name="i-box" size={54} />
          <div className="t">{t('account:history.guestTitle')}</div>
          {t('account:history.guestBody')}
          <div style={{ marginTop: 18 }}>
            <Button
              onPress={() => {
                void navigate(loginPath('/orders'))
              }}
            >
              {t('common:actions.signIn')}
            </Button>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="screen cart-wrap">
      {head}

      <div className="statrow">
        <div className="stat">
          <div className="sc">{t('account:history.pointsOnCard')}</div>
          <div className="sv num" style={{ color: 'var(--gold)' }}>
            {formatPoints(loyalty.data?.balance ?? 0)}
          </div>
        </div>
        <div className="stat">
          <div className="sc">{t('account:history.ordersCount')}</div>
          <div className="sv num">{orderList.length}</div>
        </div>
      </div>

      <div className="htabs" role="tablist" aria-label={t('account:history.title')}>
        {tabs.map(([id, label]) => (
          <Press
            key={id}
            role="tab"
            squish="chip"
            haptic="select"
            id={`htab-${id}`}
            aria-selected={tab === id}
            aria-controls="htab-panel"
            className={tab === id ? 'on' : undefined}
            onPress={() => {
              setParams(id === 'orders' ? {} : { tab: id }, { replace: true, state: from })
            }}
          >
            {label}
          </Press>
        ))}
      </div>

      <div id="htab-panel" role="tabpanel" aria-labelledby={`htab-${tab}`}>
        {tab === 'orders' ? (
          <div className="card">
            {orders.isPending ? <Loading what={t('common:state.loading')} shape="lines" /> : null}
            {orders.isError ? (
              <Failed
                error={orders.error}
                onRetry={() => {
                  void orders.refetch()
                }}
              />
            ) : null}
            {orders.data?.length === 0 ? (
              <div className="list-empty">{t('account:noOrders')}</div>
            ) : null}
            {orderList.map((order) => (
              <Press
                key={order.id}
                squish="row"
                className="ocard"
                onPress={() => {
                  void navigate(`/orders/${order.id}`)
                }}
              >
                <div className="or1">
                  <b>{t('account:orderNo', { number: order.number })}</b>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={isOrderClosed(order.status) ? 'pill stdone' : 'pill st'}>
                      {statusText(order.status, t)}
                    </span>
                    <Icon name="i-chev" size={16} />
                  </span>
                </div>
                <div className="om">
                  {formatDayTime(order.createdAt)} ·{' '}
                  {t(order.ship === 'delivery' ? 'common:ship.delivery' : 'common:ship.pickup')} ·{' '}
                  {t('catalog:goods', { count: order.lines.length })}
                </div>
                <ReservedUntil order={order} />
                <div className="or2">
                  <span>{t('account:history.sum')}</span>
                  <b className="num">{formatMoney(order.total)}</b>
                </div>
                <div className="or2">
                  <span>{t('account:history.cashback')}</span>
                  <b className="num" style={{ color: 'var(--gold)' }}>
                    +{formatPoints(order.cashback)}
                  </b>
                </div>
              </Press>
            ))}
          </div>
        ) : (
          <div className="card">
            {transactions.isPending ? (
              <Loading what={t('common:state.loading')} shape="lines" />
            ) : null}
            {transactions.data?.length === 0 ? (
              <div className="list-empty">{t('account:noPoints')}</div>
            ) : null}
            <TransactionRows transactions={transactions.data ?? []} />
          </div>
        )}
      </div>
    </section>
  )
}

/**
 * Срок брони в списке — только готовый, с датой: «соберём и забронируем на
 * сутки» сказано в карточке заказа.
 */
function ReservedUntil({ order }: { order: Parameters<typeof reservationOf>[0] }) {
  const { t } = useTranslation('account')
  const reservation = reservationOf(order, 0)
  if (!reservation || !('at' in reservation)) return null
  return (
    <div className="or2 or-reserve">
      <span>{t('account:order.reservedUntil')}</span>
      <b className="num">{formatDayTime(reservation.at)}</b>
    </div>
  )
}
